/**
 * Filtro avanzado del historial de envíos (addon `advanced_filter`).
 *
 * Un criterio a la vez y una lista de valores separados por coma: el backend
 * (`api/delivery-resume/`) los recibe como `advanced_filter_field` y
 * `advanced_filter_values`, los traduce a un `IN (...)` sobre la columna del
 * criterio y los suma a los filtros que ya están puestos. Sin el addon en el
 * plan el backend responde 403, así que las pantallas solo lo muestran cuando
 * `selectHasAdvancedFilter` lo dice.
 */

/** The criteria the backend accepts, in the order the select shows them. */
export const ADVANCED_FILTER_OPTIONS = [
  { value: 'order_id', label: 'IDs de orden', placeholder: '3899, 3898, 3897' },
  { value: 'reference_id', label: 'IDs de referencia', placeholder: '#orden1-seller, REF-002' },
  { value: 'commune', label: 'Comunas', placeholder: 'Providencia, La Florida, Ñuñoa' },
  { value: 'courier', label: 'Couriers', placeholder: 'Simpli Route, Fedex, Bluex' },
];

/** The two filter keys at rest; also what "Limpiar filtros" writes back. */
export const EMPTY_ADVANCED_FILTER = { advanced_filter_field: '', advanced_filter_values: '' };

const optionOf = (field) => ADVANCED_FILTER_OPTIONS.find((option) => option.value === field);

/** The criterion as the select names it, or '' for an unknown one. */
export const advancedFilterLabel = (field) => optionOf(field)?.label || '';

/** An example list for the criterion, shown as the input's placeholder. */
export const advancedFilterPlaceholder = (field) =>
  optionOf(field)?.placeholder || 'valor1, valor2, valor3';

/**
 * The values of a pasted list: split on commas, semicolons or line breaks,
 * trimmed, without blanks and without repeats. A spreadsheet column pasted in
 * arrives with line breaks, and that has to work as well as typing commas.
 *
 * @param {string} text
 * @returns {string[]}
 */
export const splitAdvancedFilterValues = (text) => {
  const seen = new Set();
  return String(text ?? '')
    .split(/[,;\n\r]+/)
    .map((piece) => piece.trim())
    .filter((piece) => {
      if (!piece || seen.has(piece)) return false;
      seen.add(piece);
      return true;
    });
};

/** The same list, back as the comma-separated string the backend reads. */
export const normalizeAdvancedFilterValues = (text) => splitAdvancedFilterValues(text).join(', ');

/** Whether the slice's filters carry an advanced filter worth sending. */
export const isAdvancedFilterActive = (filters) =>
  Boolean(filters?.advanced_filter_field) && splitAdvancedFilterValues(filters?.advanced_filter_values).length > 0;
